import { useState, useEffect, useRef, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { formatDate as fmtDate } from '@/lib/dateUtils';

type PaymentStatus = 'upcoming' | 'due' | 'overdue' | 'paid' | 'pending_confirmation';

interface PaymentItem {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  amount: number;
  currency: string;
  due_date: string | null;
  status: PaymentStatus;
  payment_reference: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface PaymentConfirmation {
  id: string;
  payment_schedule_id: string;
  notified_by_name: string | null;
  note: string | null;
  payment_date: string | null;
  amount_paid: number | null;
  reference_text: string | null;
  status: string;
  created_at: string;
}

interface PaymentsTabProps {
  projectId: string;
}

interface EditFormState {
  title: string;
  description: string;
  amount: string;
  due_date: string;
  status: PaymentStatus;
  payment_reference: string;
}

const emptyForm: EditFormState = {
  title: '',
  description: '',
  amount: '',
  due_date: '',
  status: 'upcoming',
  payment_reference: '',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', minimumFractionDigits: 0 }).format(amount);
}

function daysUntilDue(dueDateStr: string | null): number | null {
  if (!dueDateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDateStr);
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86400000);
}

interface PaymentEditFormProps {
  editingId: string | null;
  form: EditFormState;
  setForm: Dispatch<SetStateAction<EditFormState>>;
  saving: boolean;
  statusOptions: { value: PaymentStatus; label: string }[];
  onCancel: () => void;
  onSave: () => void;
  formContainerRef?: RefObject<HTMLDivElement | null>;
  titleInputRef?: RefObject<HTMLInputElement | null>;
}

/** Module-level form keeps a stable component identity so inputs do not remount on each keystroke. */
function PaymentEditForm({
  editingId,
  form,
  setForm,
  saving,
  statusOptions,
  onCancel,
  onSave,
  formContainerRef,
  titleInputRef,
}: PaymentEditFormProps) {
  const { t } = useTranslation();

  return (
    <div
      ref={formContainerRef}
      className="bg-white rounded-2xl border border-[#E8621A]/30 p-4 space-y-3"
    >
      <h4 className="font-bold text-[#1A1A1A] text-sm">
        {editingId ? t('payments.editPayment') : t('payments.addPayment')}
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-[#374151] mb-1">
            {t('payments.titleLabel')} <span className="text-red-500">{t('payments.required')}</span>
          </label>
          <input
            ref={titleInputRef}
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Deposit, Frame Payment, Final Payment"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs font-semibold text-[#374151] mb-1">
            {t('payments.descriptionLabel')}{' '}
            <span className="text-[#9CA3AF] font-normal">{t('payments.optional')}</span>
          </label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="e.g. Progress payment upon completion of framing"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#374151] mb-1">
            {t('payments.amountLabel')} <span className="text-red-500">{t('payments.required')}</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm">$</span>
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="0"
              min="0"
              step="100"
              className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#374151] mb-1">{t('payments.dueDateLabel')}</label>
          <input
            type="date"
            value={form.due_date}
            onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#374151] mb-1">{t('payments.statusLabel')}</label>
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as PaymentStatus }))}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors cursor-pointer"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[#374151] mb-1">
            {t('payments.paymentReferenceLabel')}{' '}
            <span className="text-[#9CA3AF] font-normal">{t('payments.optional')}</span>
          </label>
          <input
            type="text"
            value={form.payment_reference}
            onChange={(e) => setForm((f) => ({ ...f, payment_reference: e.target.value }))}
            placeholder="e.g. INV-001"
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-[#E8621A] transition-colors"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[#374151] text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          {t('payments.cancel')}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !form.title.trim() || !form.amount}
          className="flex-1 py-2.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-1.5"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          {editingId ? t('payments.saveChanges') : t('payments.addPayment')}
        </button>
      </div>
    </div>
  );
}

export default function PaymentsTab({ projectId }: PaymentsTabProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // ── Translated configs (reactive to language) ──
  const STATUS_OPTIONS: { value: PaymentStatus; label: string }[] = [
    { value: 'upcoming', label: t('payments.statusUpcoming') },
    { value: 'due', label: t('payments.statusDueNow') },
    { value: 'overdue', label: t('payments.statusOverdue') },
    { value: 'paid', label: t('payments.statusPaid') },
    { value: 'pending_confirmation', label: t('payments.statusPendingConfirmation') },
  ];

  const statusConfig: Record<PaymentStatus, { bg: string; text: string; label: string }> = {
    paid:                 { bg: '#F0FDF4', text: '#059669', label: t('payments.statusPaid') },
    due:                  { bg: '#FEF0E7', text: '#E8621A', label: t('payments.statusDueNow') },
    overdue:              { bg: '#FEF2F2', text: '#EF4444', label: t('payments.statusOverdue') },
    upcoming:             { bg: '#F9FAFB', text: '#9CA3AF', label: t('payments.statusUpcoming') },
    pending_confirmation: { bg: '#FFFBEB', text: '#F59E0B', label: t('payments.statusPendingConfirmation') },
  };

  function getDueBadge(status: PaymentStatus, dueDateStr: string | null): { text: string; color: string } | null {
    if (status === 'paid' || status === 'pending_confirmation') return null;
    const days = daysUntilDue(dueDateStr);
    if (days === null) return null;
    if (days < 0) return { text: `${Math.abs(days)}d ${t('payments.statusOverdue').toLowerCase()}`, color: '#EF4444' };
    if (days === 0) return { text: t('payments.dueToday'), color: '#EF4444' };
    if (days <= 3) return { text: t('payments.dueInDays').replace('{n}', String(days)), color: '#E8621A' };
    if (days <= 7) return { text: t('payments.dueInDays').replace('{n}', String(days)), color: '#F59E0B' };
    return null;
  }

  const [projectCode, setProjectCode] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [confirmations, setConfirmations] = useState<PaymentConfirmation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EditFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [expandedConfirmId, setExpandedConfirmId] = useState<string | null>(null);
  const addFormContainerRef = useRef<HTMLDivElement>(null);
  const addTitleInputRef = useRef<HTMLInputElement>(null);
  const addFormAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    resolveProjectCode();
  }, [projectId]);

  useEffect(() => {
    if (!showAddForm) return;
    const frame = requestAnimationFrame(() => {
      addFormContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      requestAnimationFrame(() => {
        addTitleInputRef.current?.focus({ preventScroll: true });
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [showAddForm]);

  async function resolveProjectCode() {
    setLoading(true);
    const { data: proj, error: projErr } = await supabase
      .from('jphph_projects')
      .select('project_code')
      .eq('id', projectId)
      .maybeSingle();

    if (projErr) console.error('[PaymentsTab] project lookup error:', projErr);

    const code = proj?.project_code ?? null;
    setProjectCode(code);

    if (code) {
      await loadData(code);
    } else {
      setPayments([]);
      setConfirmations([]);
      setLoading(false);
    }
  }

  async function loadData(code: string) {
    const [{ data: schedules, error: sErr }, { data: confs, error: cErr }] = await Promise.all([
      supabase.from('jphph_project_payment_schedules').select('*').eq('project_id', code).order('sort_order', { ascending: true }),
      supabase.from('jphph_project_payment_confirmations').select('*').eq('project_id', code).order('created_at', { ascending: false }),
    ]);

    if (sErr) console.error('[PaymentsTab] schedules error:', sErr);
    if (cErr) console.error('[PaymentsTab] confirmations error:', cErr);

    setPayments((schedules as PaymentItem[]) || []);
    setConfirmations((confs as PaymentConfirmation[]) || []);
    setLoading(false);
  }

  function startEdit(item: PaymentItem) {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description || '',
      amount: String(item.amount),
      due_date: item.due_date || '',
      status: item.status,
      payment_reference: item.payment_reference || '',
    });
    setShowAddForm(false);
  }

  function startAdd() {
    setShowAddForm(true);
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleSave() {
    if (!form.title.trim() || !form.amount || !projectCode) return;
    setSaving(true);
    const maxOrder = payments.length > 0 ? Math.max(...payments.map((p) => p.sort_order)) : 0;

    let updatedItem: PaymentItem | null = null;
    let wasNew = false;

    if (editingId) {
      const { data: updated } = await supabase
        .from('jphph_project_payment_schedules')
        .update({
          title: form.title.trim(),
          description: form.description.trim() || null,
          amount: parseFloat(form.amount),
          due_date: form.due_date || null,
          status: form.status,
          payment_reference: form.payment_reference.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingId)
        .select()
        .maybeSingle();
      if (updated) {
        updatedItem = updated as PaymentItem;
        setPayments((prev) => prev.map((p) => p.id === editingId ? updatedItem! : p));
      }
      setEditingId(null);
    } else {
      const { data: created } = await supabase
        .from('jphph_project_payment_schedules')
        .insert({
          project_id: projectCode,
          title: form.title.trim(),
          description: form.description.trim() || null,
          amount: parseFloat(form.amount),
          currency: 'NZD',
          due_date: form.due_date || null,
          status: form.status,
          payment_reference: form.payment_reference.trim() || null,
          sort_order: maxOrder + 1,
        })
        .select()
        .maybeSingle();
      if (created) {
        updatedItem = created as PaymentItem;
        setPayments((prev) => [...prev, updatedItem!]);
      }
      wasNew = true;
      setShowAddForm(false);
    }

    if (updatedItem) {
      await createNotification({
        project_id: projectId,
        notification_type: 'payment',
        title: `Payment ${wasNew ? 'added' : 'updated'}: ${updatedItem.title}`,
        body: `Amount: ${formatCurrency(updatedItem.amount)}${updatedItem.due_date ? ' · Due: ' + fmtDate(updatedItem.due_date, lang) : ''}`,
        target_role: 'client',
        link_path: `/client/payments`,
        created_by: undefined,
      });
    }

    setForm(emptyForm);
    setSaving(false);
  }

  async function handleDelete(id: string) {
    await supabase.from('jphph_project_payment_schedules').delete().eq('id', id);
    setPayments((prev) => prev.filter((p) => p.id !== id));
    setDeleteConfirmId(null);
  }

  async function confirmPayment(confirmationId: string, paymentId: string) {
    await supabase.from('jphph_project_payment_confirmations').update({ status: 'confirmed', reviewed_at: new Date().toISOString() }).eq('id', confirmationId);
    await supabase.from('jphph_project_payment_schedules').update({ status: 'paid', updated_at: new Date().toISOString() }).eq('id', paymentId);

    setPayments((prev) => prev.map((p) => p.id === paymentId ? { ...p, status: 'paid' as PaymentStatus } : p));
    setConfirmations((prev) => prev.map((c) => c.id === confirmationId ? { ...c, status: 'confirmed' } : c));
    setExpandedConfirmId(null);

    const payment = payments.find((p) => p.id === paymentId);
    if (payment) {
      await createNotification({
        project_id: projectId,
        notification_type: 'payment',
        title: `Payment confirmed: ${payment.title}`,
        body: t('payments.mgmtConfirmedReceipt'),
        target_role: 'client',
        link_path: `/client/payments`,
      });
    }
  }

  const total = payments.reduce((s, p) => s + p.amount, 0);
  const paid = payments.filter((p) => p.status === 'paid').reduce((s, p) => s + p.amount, 0);
  const pendingConfs = confirmations.filter((c) => c.status === 'submitted');
  const overduePayments = payments.filter((p) => p.status === 'overdue');
  const duePayments = payments.filter((p) => p.status === 'due');
  const approachingPayments = payments.filter((p) => {
    if (p.status === 'paid' || p.status === 'overdue' || p.status === 'due') return false;
    const days = daysUntilDue(p.due_date);
    return days !== null && days >= 0 && days <= 7;
  });

  const getConfirmation = (paymentId: string) =>
    confirmations.find((c) => c.payment_schedule_id === paymentId && c.status === 'submitted');

  const resetForm = () => {
    const wasAdding = showAddForm;
    setShowAddForm(false);
    setEditingId(null);
    setForm(emptyForm);
    if (wasAdding) {
      requestAnimationFrame(() => {
        addFormAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  const editFormProps = {
    editingId,
    form,
    setForm,
    saving,
    statusOptions: STATUS_OPTIONS,
    onCancel: resetForm,
    onSave: handleSave,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      {payments.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <p className="text-[#9CA3AF] text-[10px] mb-0.5">{t('payments.total')}</p>
            <p className="text-[#1A1A1A] font-bold text-sm">{formatCurrency(total)}</p>
          </div>
          <div className="bg-[#F0FDF4] rounded-xl border border-[#BBF7D0] p-3 text-center">
            <p className="text-[#059669] text-[10px] mb-0.5">{t('payments.paid')}</p>
            <p className="text-[#059669] font-bold text-sm">{formatCurrency(paid)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
            <p className="text-[#9CA3AF] text-[10px] mb-0.5">{t('payments.remaining')}</p>
            <p className="text-[#1A1A1A] font-bold text-sm">{formatCurrency(total - paid)}</p>
          </div>
        </div>
      )}

      {/* Overdue alert */}
      {overduePayments.length > 0 && (
        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-2xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-[#EF4444] text-base"></i>
          </div>
          <div>
            <p className="text-[#EF4444] font-bold text-sm">{overduePayments.length} {t('payments.statusOverdue')} payment{overduePayments.length !== 1 ? 's' : ''}</p>
            <p className="text-[#374151] text-xs mt-0.5">{overduePayments.map((p) => p.title).join(', ')} — {t('payments.clientNotified')}</p>
          </div>
        </div>
      )}

      {/* Due soon alert */}
      {duePayments.length > 0 && (
        <div className="bg-[#FEF0E7] border border-[#FDBA74] rounded-2xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#E8621A]/10 flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-[#E8621A] text-base"></i>
          </div>
          <div>
            <p className="text-[#E8621A] font-bold text-sm">{duePayments.length} payment{duePayments.length !== 1 ? 's' : ''} {t('payments.statusDueNow').toLowerCase()}</p>
            <p className="text-[#374151] text-xs mt-0.5">{duePayments.map((p) => p.title).join(', ')}</p>
          </div>
        </div>
      )}

      {/* Approaching due */}
      {approachingPayments.length > 0 && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl p-3 flex items-center gap-2">
          <i className="ri-calendar-event-line text-[#F59E0B] text-sm"></i>
          <span className="text-[#374151] text-xs">
            <strong className="text-[#F59E0B]">{approachingPayments.length} payment{approachingPayments.length !== 1 ? 's' : ''}</strong> due within 7 days: {approachingPayments.map((p) => p.title).join(', ')}
          </span>
        </div>
      )}

      {/* Pending confirmations alert */}
      {pendingConfs.length > 0 && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-alarm-warning-line text-[#F59E0B] text-base"></i>
            <span className="text-[#F59E0B] font-bold text-sm">{pendingConfs.length} payment notification{pendingConfs.length !== 1 ? 's' : ''} {t('payments.statusPendingConfirmation').toLowerCase()}</span>
          </div>
          <p className="text-[#374151] text-xs">{t('payments.reviewAndConfirm')}</p>
        </div>
      )}

      {/* Payment list */}
      {payments.length === 0 && !showAddForm ? (
        <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-gray-200">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-bank-card-line text-4xl"></i>
          </div>
          <p className="text-[#374151] font-semibold text-sm mb-1">{t('payments.noPaymentSchedule')}</p>
          <p className="text-[#9CA3AF] text-xs mb-4">{t('payments.addPaymentMilestones')}</p>
          <button onClick={startAdd} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i>{t('payments.addFirstPayment')}
          </button>
        </div>
      ) : payments.length > 0 ? (
        <div className="space-y-2.5">
          {payments.map((payment, idx) => {
            const cfg = statusConfig[payment.status] || statusConfig.upcoming;
            const confirmation = getConfirmation(payment.id);
            const isEditing = editingId === payment.id;
            const isDeleteConfirm = deleteConfirmId === payment.id;
            const isExpandedConf = expandedConfirmId === payment.id;

            if (isEditing) {
              return <PaymentEditForm key={`payment-edit-${payment.id}`} {...editFormProps} />;
            }

            return (
              <div
                key={payment.id}
                className={`bg-white rounded-2xl border overflow-hidden ${
                  payment.status === 'overdue' ? 'border-[#EF4444]/30' :
                  payment.status === 'due' ? 'border-[#E8621A]/30' :
                  confirmation ? 'border-[#F59E0B]/40' :
                  'border-gray-100'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ backgroundColor: cfg.bg, color: cfg.text }}>
                      {payment.status === 'paid' ? <i className="ri-check-line text-xs"></i> : idx + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-[#1A1A1A] font-bold text-sm">{payment.title}</h4>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap" style={{ backgroundColor: cfg.bg, color: cfg.text }}>
                          {cfg.label}
                        </span>
                      </div>

                      {payment.description && <p className="text-[#9CA3AF] text-xs mb-2">{payment.description}</p>}

                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <p className="text-[#9CA3AF] text-[10px]">{t('payments.amountLabel')}</p>
                          <p className="text-[#1A1A1A] font-bold text-sm">{formatCurrency(payment.amount)}</p>
                        </div>
                        {payment.due_date && (
                          <div>
                            <p className="text-[#9CA3AF] text-[10px]">{t('payments.dueDateLabel')}</p>
                            <p className="text-[#374151] font-medium text-sm">{fmtDate(payment.due_date, lang)}</p>
                          </div>
                        )}
                        {payment.payment_reference && (
                          <div>
                            <p className="text-[#9CA3AF] text-[10px]">{t('payments.referenceLabel')}</p>
                            <p className="text-[#374151] font-medium text-sm">{payment.payment_reference}</p>
                          </div>
                        )}
                      </div>

                      {(() => {
                        const badge = getDueBadge(payment.status, payment.due_date);
                        if (!badge) return null;
                        return (
                          <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: `${badge.color}15` }}>
                            <i className="ri-alarm-warning-line text-[10px]" style={{ color: badge.color }}></i>
                            <span className="text-[10px] font-bold" style={{ color: badge.color }}>{badge.text}</span>
                          </div>
                        );
                      })()}

                      {confirmation && (
                        <div className="mt-3">
                          <button onClick={() => setExpandedConfirmId(isExpandedConf ? null : payment.id)} className="w-full flex items-center gap-2 bg-[#FFFBEB] border border-[#FDE68A] rounded-xl px-3 py-2.5 cursor-pointer text-left">
                            <i className="ri-notification-3-line text-[#F59E0B] text-sm flex-shrink-0"></i>
                            <div className="flex-1 min-w-0">
                              <p className="text-[#F59E0B] text-xs font-bold">{t('payments.clientNotifiedPayment')}</p>
                              <p className="text-[#9CA3AF] text-[10px]">{confirmation.notified_by_name || 'Client'} · {fmtDate(confirmation.created_at, lang)}</p>
                            </div>
                            <i className={`${isExpandedConf ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-[#9CA3AF] flex-shrink-0`}></i>
                          </button>

                          {isExpandedConf && (
                            <div className="mt-2 bg-[#FFFBEB] rounded-xl p-3 space-y-2">
                              {confirmation.payment_date && (
                                <div className="flex items-center justify-between">
                                  <span className="text-[#9CA3AF] text-xs">{t('payments.paymentDateLabel')}</span>
                                  <span className="text-[#374151] text-xs font-medium">{fmtDate(confirmation.payment_date, lang)}</span>
                                </div>
                              )}
                              {confirmation.amount_paid && (
                                <div className="flex items-center justify-between">
                                  <span className="text-[#9CA3AF] text-xs">{t('payments.amountPaidLabel')}</span>
                                  <span className="text-[#374151] text-xs font-medium">{formatCurrency(confirmation.amount_paid)}</span>
                                </div>
                              )}
                              {confirmation.reference_text && (
                                <div className="flex items-center justify-between">
                                  <span className="text-[#9CA3AF] text-xs">{t('payments.referenceLabel')}</span>
                                  <span className="text-[#374151] text-xs font-medium">{confirmation.reference_text}</span>
                                </div>
                              )}
                              {confirmation.note && (
                                <div>
                                  <span className="text-[#9CA3AF] text-xs">{t('payments.noteLabel')}</span>
                                  <p className="text-[#374151] text-xs mt-0.5">{confirmation.note}</p>
                                </div>
                              )}
                              <button onClick={() => confirmPayment(confirmation.id, payment.id)} className="w-full py-2 rounded-xl bg-[#059669] text-white text-xs font-bold cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5">
                                <i className="ri-checkbox-circle-line"></i>
                                {t('payments.confirmPaymentReceived')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => startEdit(payment)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors" title={t('btn.edit')}>
                        <i className="ri-edit-line text-[#9CA3AF] text-sm"></i>
                      </button>
                      {isDeleteConfirm ? (
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleDelete(payment.id)} className="px-2 py-1 rounded-lg bg-[#EF4444] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap">
                            {t('payments.delete')}
                          </button>
                          <button onClick={() => setDeleteConfirmId(null)} className="px-2 py-1 rounded-lg bg-gray-100 text-[#6B7280] text-[10px] cursor-pointer whitespace-nowrap">
                            {t('payments.cancel')}
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirmId(payment.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#FEF2F2] cursor-pointer transition-colors" title={t('btn.delete')}>
                          <i className="ri-delete-bin-line text-[#9CA3AF] hover:text-[#EF4444] text-sm transition-colors"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      <div ref={addFormAnchorRef}>
        {!editingId && (payments.length > 0 || showAddForm) && (
          showAddForm ? (
            <PaymentEditForm
              key="payment-add-form"
              {...editFormProps}
              editingId={null}
              formContainerRef={addFormContainerRef}
              titleInputRef={addTitleInputRef}
            />
          ) : (
            <button
              onClick={startAdd}
              className="w-full py-3 rounded-2xl border-2 border-dashed border-gray-200 text-[#9CA3AF] text-sm font-medium cursor-pointer hover:border-[#E8621A] hover:text-[#E8621A] transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line text-base"></i>
              {t('payments.addPaymentItem')}
            </button>
          )
        )}
      </div>
    </div>
  );
}